import React from 'react';
import { Zap, Clock, MessageSquareQuote, ArrowRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import Section from './Section';

const Speed: React.FC = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const timeline = [
    { label: '打ち合わせ当日', desc: 'ヒアリング・課題整理', accent: true },
    { label: '翌日〜3日以内', desc: '計画書（PDF）をお送り', accent: true },
    { label: '1〜2週間', desc: '動くプロトタイプを公開', accent: false },
    { label: '以降', desc: 'フィードバック→改善をサクサク繰り返し', accent: false },
  ];

  return (
    <Section id="speed" className="py-20 lg:py-28 bg-[#1B2A4A]">
      <div ref={ref} className={`mx-auto max-w-6xl px-5 lg:px-6 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/40 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
            <Zap className="h-4 w-4 text-[#B8860B]" />
            <span className="text-sm font-semibold text-[#B8860B]">スピード感</span>
          </div>
          <h2 className="text-3xl font-bold lg:text-4xl text-[#F5F3EF]">
            <span className="block mb-3">お客様の第一声は</span>
            <span className="text-[#B8860B] text-4xl lg:text-5xl">「早っ！」</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* 左: テキスト */}
          <div>
            <div className="space-y-5 text-[#F5F3EF]/80 leading-relaxed text-justify">
              <p>
                打ち合わせの後、次の動きが遅い。見積もりが出てくるまで何週間もかかる。——そんな経験はありませんか？
              </p>
              <p>
                Senriganでは、<span className="text-[#B8860B] font-semibold">打ち合わせ後、早ければ翌日、遅くても3日以内</span>に、見やすい計画書をPDFでお送りしています。
              </p>
              <p>
                その後の開発も、動くプロトタイプからスピード感を持ってサクサク進めます。これがAI時代のスピード感です。
              </p>
            </div>

            {/* 吹き出し風の引用 */}
            <div className="mt-8 rounded-xl border-l-4 border-[#B8860B] bg-white/5 px-5 py-4">
              <div className="flex items-start gap-3">
                <MessageSquareQuote className="h-5 w-5 text-[#B8860B] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#F5F3EF] italic">
                    「逆に、ゆっくり進めたい方には合わないかもしれません。」
                  </p>
                  <p className="text-[#F5F3EF]/50 text-sm mt-1">— 代表 宮川</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-lg bg-[#B8860B] px-6 py-3 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/20"
              >
                スピード感を体感する
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* 右: タイムライン */}
          <div className="rounded-2xl border border-[#F5F3EF]/10 bg-white/5 backdrop-blur-sm p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Clock className="h-5 w-5 text-[#B8860B]" />
              <span className="text-xs font-bold tracking-widest text-[#B8860B]">プロジェクトの流れ</span>
            </div>
            <div className="space-y-0">
              {timeline.map((step, index) => (
                <div key={index} className="flex gap-4">
                  {/* 縦線 + ドット */}
                  <div className="flex flex-col items-center">
                    <div className={`w-3.5 h-3.5 rounded-full shrink-0 ${step.accent ? 'bg-[#B8860B]' : 'bg-[#4A6FA5]'} border-2 border-[#1B2A4A]`} />
                    {index < timeline.length - 1 && (
                      <div className="w-px flex-1 bg-[#F5F3EF]/15 my-1" />
                    )}
                  </div>
                  {/* コンテンツ */}
                  <div className={`pb-6 ${index === timeline.length - 1 ? 'pb-0' : ''}`}>
                    <div className={`text-sm font-bold ${step.accent ? 'text-[#B8860B]' : 'text-[#F5F3EF]/60'}`}>
                      {step.label}
                    </div>
                    <div className="text-[#F5F3EF] font-medium mt-0.5">
                      {step.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Speed;
